import { useInViewport, usePrefersReducedMotion } from 'hooks';
import { useEffect, useMemo, useRef } from 'react';
import {
  AdditiveBlending,
  AmbientLight,
  BackSide,
  BufferGeometry,
  Color,
  DirectionalLight,
  DoubleSide,
  Float32BufferAttribute,
  Group,
  Line,
  LineBasicMaterial,
  LineLoop,
  LineSegments,
  Mesh,
  MeshBasicMaterial,
  MeshPhongMaterial,
  PerspectiveCamera,
  Raycaster,
  RingBufferGeometry,
  Scene,
  SphereBufferGeometry,
  Vector2,
  Vector3,
  WebGLRenderer,
} from 'three';
import { feature } from 'topojson-client';
import { rgbToThreeColor } from 'utils/style';
import { cleanRenderer, cleanScene, removeLights } from 'utils/three';
import landTopo from 'world-atlas/land-110m.json';

/* ── Globe constants ─────────────────────────────────────────────────────── */

const GLOBE_RADIUS = 1;
const GRATICULE_RADIUS = GLOBE_RADIUS * 1.001; // holographic grid, just off surface
const COAST_RADIUS = GLOBE_RADIUS * 1.002;
const MARKER_RADIUS = GLOBE_RADIUS * 1.012;
const RIM_RADIUS = GLOBE_RADIUS * 1.022; // crisp thin fresnel rim shell
const ATMOSPHERE_RADIUS = GLOBE_RADIUS * 1.06; // soft halo shell
const ARC_PEAK = GLOBE_RADIUS * 0.18; // extra height at the arc's midpoint
const ARC_STEPS = 64;
const AUTO_ROTATE_SPEED = 0.0015;
const MAX_TILT = 0.5; // clamp for drag-to-rotate vertical tilt (radians)
const LABEL_FADE = 0.12; // opacity easing factor per frame for label fades

/* Holographic graticule (lat/long grid) tuning — sparse + faint so it reads as
   an instrument grid, not a bright cage. */
const GRATICULE_MERIDIANS = 12; // longitude lines
const GRATICULE_PARALLELS = 6; // latitude lines (excluding poles)
const GRATICULE_SEGMENTS = 90; // smoothness per line
const GRATICULE_OPACITY = 0.16; // base; eased toward this each frame
const GRATICULE_SHIMMER = 0.05; // ± shimmer amplitude on the grid opacity

/* Radar-ping marker rings — thin expanding rings, strongest on the active city.
   Each ring scales from PING_MIN→PING_MAX over PING_PERIOD then resets. */
const PING_MIN_SCALE = 1;
const PING_MAX_SCALE = 3.4;
const PING_PERIOD = 2600; // ms for one expand-and-fade cycle
const PING_ACTIVE_OPACITY = 0.5; // peak opacity for the active city's ping
const PING_IDLE_OPACITY = 0.14; // peak opacity for non-active cities' pings

/* Flowing-arc travelling pulse — a small glowing dot rides each arc. */
const FLOW_SPEED = 0.00022; // progress per ms along the arc (0..1 loop)
const FLOW_TAIL = 6; // vertices behind the head that fade in as a comet tail

/* Drag-to-spin tuning */
const DRAG_SENSITIVITY = 0.005; // pixels → radians (≈1:1 feel)
const DRAG_THRESHOLD = 4; // px of movement before a gesture becomes a drag
const MOMENTUM_DECAY = 0.95; // per-frame velocity decay after release
const MOMENTUM_MIN = 0.00025; // below this angular velocity, momentum stops

/* Rotate-to-face tuning (Task 4): when a city is focused, ease the group's
   rotation so that city faces the camera (+Z), then hold there. */
const FOCUS_EASE = 0.12; // per-frame easing toward the target orientation
const TWO_PI = Math.PI * 2;

/* Wrap an angle delta into [-π, π] so the tween always takes the short way. */
const wrapAngle = a => {
  let v = a % TWO_PI;
  if (v > Math.PI) v -= TWO_PI;
  if (v < -Math.PI) v += TWO_PI;
  return v;
};

/* Design tokens → concrete THREE colors (WebGL can't read CSS vars).
   The globe now reads as a dark, polished display piece on the paper page. */
const RGB_NAVY_DEEP = '10 16 33'; // sphere base — deep navy
const RGB_NAVY_EMISSIVE = '14 22 44'; // faint emissive so it isn't pure black
const RGB_COAST = '170 194 236'; // luminous soft blue-white continents
const RGB_RIM = '40 90 170'; // sapphire soft halo

/* Holographic instrument tones — thin electric sapphire→cyan lines */
const RGB_GRATICULE = '70 130 210'; // grid base — electric sapphire
const RGB_RIM_LIGHT = '90 170 230'; // crisp fresnel rim — cyan-sapphire
const RGB_PING = '110 185 235'; // radar-ping rings — cool cyan

/* Markers lifted to read on navy */
const RGB_EMERALD_LIFT = '60 180 130'; // Paris (work)
const RGB_SAPPHIRE_LIFT = '90 150 240'; // work (sapphire accent)
const RGB_STUDY = '150 165 195'; // study cities — soft light blue-grey

/* Arc colours, brightened so they glow on dark */
const RGB_ARC_EMERALD = '70 195 145';
const RGB_ARC_SAPPHIRE = '110 165 245';
const RGB_ARC_STUDY = '165 180 210';

const RGB_KEY_LIGHT = '215 232 255'; // cool azure key (matches DisplacementSphere)
const RGB_AMBIENT = '120 140 185';

/* Per-accent marker + arc colour lookups */
const MARKER_RGB = {
  emerald: RGB_EMERALD_LIFT,
  sapphire: RGB_SAPPHIRE_LIFT,
  muted: RGB_STUDY,
};

const ARC_RGB = {
  emerald: RGB_ARC_EMERALD,
  sapphire: RGB_ARC_SAPPHIRE,
  muted: RGB_ARC_STUDY,
};

/* Cities (lon, lat) — same data the list owns, kept here for the geometry.
   `labelOffset` nudges the HTML tracking label in screen px so geographically
   close pairs don't overlap. */
const CITIES = [
  { id: 'montreal', lonlat: [-73.5674, 45.5019], year: 2019, accent: 'muted' },
  { id: 'paris', lonlat: [2.3522, 48.8566], year: 2020, accent: 'emerald' },
  { id: 'barcelona', lonlat: [2.1734, 41.3851], year: 2021, accent: 'muted' },
];

/* lon/lat → 3D position on a sphere of radius r */
const toXYZ = (lon, lat, r) => {
  const phi = ((90 - lat) * Math.PI) / 180;
  const theta = ((lon + 180) * Math.PI) / 180;
  return new Vector3(
    -r * Math.sin(phi) * Math.cos(theta),
    r * Math.cos(phi),
    r * Math.sin(phi) * Math.sin(theta)
  );
};

const threeColor = rgb => new Color(...rgbToThreeColor(rgb));

/* Slerp between two unit vectors for a great-circle interpolation */
const slerp = (from, to, t) => {
  const dot = Math.min(1, Math.max(-1, from.dot(to)));
  const omega = Math.acos(dot);
  if (omega < 1e-6) return from.clone();
  const sinOmega = Math.sin(omega);
  const a = Math.sin((1 - t) * omega) / sinOmega;
  const b = Math.sin(t * omega) / sinOmega;
  return from.clone().multiplyScalar(a).add(to.clone().multiplyScalar(b));
};

/* Build a flat array of coastline ring vertex-loops from the land TopoJSON.
   Computed once (it's the heavy bit). Each entry is a flat [x,y,z,...] array. */
const buildCoastlineRings = () => {
  const land = feature(landTopo, landTopo.objects.land);
  const rings = [];

  const addRing = coords => {
    const positions = [];
    for (const [lon, lat] of coords) {
      const v = toXYZ(lon, lat, COAST_RADIUS);
      positions.push(v.x, v.y, v.z);
    }
    if (positions.length >= 6) rings.push(positions);
  };

  const addGeometry = geom => {
    if (!geom) return;
    const polygons =
      geom.type === 'MultiPolygon' ? geom.coordinates : [geom.coordinates];
    for (const polygon of polygons) {
      for (const ring of polygon) {
        addRing(ring);
      }
    }
  };

  // `land` is a GeometryCollection → feature() yields a FeatureCollection.
  // Handle both a FeatureCollection and a single Feature defensively.
  if (Array.isArray(land.features)) {
    for (const f of land.features) addGeometry(f.geometry);
  } else {
    addGeometry(land.geometry);
  }

  return rings;
};

/* Build a single bowed great-circle arc Line between two cities */
const buildArc = (fromCity, toCity) => {
  const from = toXYZ(fromCity.lonlat[0], fromCity.lonlat[1], 1).normalize();
  const to = toXYZ(toCity.lonlat[0], toCity.lonlat[1], 1).normalize();

  const positions = [];
  const colors = [];
  const colorFrom = threeColor(ARC_RGB[fromCity.accent] || RGB_ARC_SAPPHIRE);
  const colorTo = threeColor(ARC_RGB[toCity.accent] || RGB_ARC_SAPPHIRE);

  for (let i = 0; i <= ARC_STEPS; i += 1) {
    const t = i / ARC_STEPS;
    const point = slerp(from, to, t);
    // Bell curve: 0 at the ends, ARC_PEAK at the midpoint, lifts the arc out
    const lift = Math.sin(Math.PI * t) * ARC_PEAK;
    const r = MARKER_RADIUS + lift;
    point.multiplyScalar(r);
    positions.push(point.x, point.y, point.z);

    const c = colorFrom.clone().lerp(colorTo, t);
    colors.push(c.r, c.g, c.b);
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
  geometry.setAttribute('color', new Float32BufferAttribute(colors, 3));
  return geometry;
};

/* Build a sample of points along a bowed great-circle arc, so a flowing pulse
   can ride the same path the visible arc traces. Returns an array of Vector3. */
const buildArcPath = (fromCity, toCity) => {
  const from = toXYZ(fromCity.lonlat[0], fromCity.lonlat[1], 1).normalize();
  const to = toXYZ(toCity.lonlat[0], toCity.lonlat[1], 1).normalize();
  const path = [];
  for (let i = 0; i <= ARC_STEPS; i += 1) {
    const t = i / ARC_STEPS;
    const point = slerp(from, to, t);
    const lift = Math.sin(Math.PI * t) * ARC_PEAK;
    point.multiplyScalar(MARKER_RADIUS + lift);
    path.push(point);
  }
  return path;
};

/* Build a sparse lat/long graticule as a single LineSegments geometry on a
   sphere of radius r. Meridians (longitude) + parallels (latitude). Kept low
   in segment count so it reads as a holographic instrument grid, not a cage. */
const buildGraticule = r => {
  const positions = [];

  const pushSegment = (a, b) => {
    positions.push(a.x, a.y, a.z, b.x, b.y, b.z);
  };

  // Meridians: vary lat from -85..85 at evenly spaced longitudes
  for (let m = 0; m < GRATICULE_MERIDIANS; m += 1) {
    const lon = -180 + (360 / GRATICULE_MERIDIANS) * m;
    let prev = null;
    for (let s = 0; s <= GRATICULE_SEGMENTS; s += 1) {
      const lat = -85 + (170 / GRATICULE_SEGMENTS) * s;
      const v = toXYZ(lon, lat, r);
      if (prev) pushSegment(prev, v);
      prev = v;
    }
  }

  // Parallels: full rings of constant latitude
  for (let p = 1; p <= GRATICULE_PARALLELS; p += 1) {
    const lat = -90 + (180 / (GRATICULE_PARALLELS + 1)) * p;
    let prev = null;
    for (let s = 0; s <= GRATICULE_SEGMENTS; s += 1) {
      const lon = -180 + (360 / GRATICULE_SEGMENTS) * s;
      const v = toXYZ(lon, lat, r);
      if (prev) pushSegment(prev, v);
      prev = v;
    }
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
  return geometry;
};

/* ── Component ───────────────────────────────────────────────────────────── */

const CitiesGlobe = ({
  activeLocation,
  onActivate,
  onDeactivate,
  prefersReducedMotion: prefersReducedMotionProp,
}) => {
  const canvasRef = useRef();
  const wrapRef = useRef();
  const labelLayerRef = useRef();
  const renderer = useRef();
  const camera = useRef();
  const scene = useRef();
  const group = useRef();
  const lights = useRef();
  const markers = useRef({}); // id → Mesh
  const markerGlows = useRef({}); // id → Mesh (additive glow halo)
  const pingRings = useRef({}); // id → Mesh (radar-ping ring, animated)
  const graticuleMat = useRef(null); // holographic grid material (shimmer)
  const flowPulses = useRef([]); // [{ mesh, path }] travelling arc pulses
  const labelEls = useRef({}); // id → HTMLElement
  const labelOpacity = useRef({}); // id → current eased opacity
  const disposables = useRef([]); // geometries/materials to dispose on unmount
  const dragState = useRef({
    pointerDown: false, // pointer is pressed but maybe below threshold
    dragging: false, // movement passed the threshold → spinning the globe
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
    paused: false, // suppress auto-rotate while dragging
    velX: 0, // angular velocity (rad/frame) carried into momentum on release
    velY: 0,
  });
  const activeRef = useRef(activeLocation);
  const pointerOverRef = useRef(false); // pointer is hovering the canvas (Task 2)
  // id of a marker the canvas raycast is currently hovering directly on the
  // globe (null when the pointer isn't over a marker). When the active city was
  // set by this direct-on-globe hover, we highlight but DON'T rotate-to-face —
  // rotating would move the marker out from under the cursor and stall the hover.
  const raycastHoverRef = useRef(null);
  const clockRef = useRef(0); // accumulated ms for ping/flow/shimmer phases
  const lastTsRef = useRef(0); // previous rAF timestamp for delta-time

  const ownReducedMotion = usePrefersReducedMotion();
  const prefersReducedMotion = prefersReducedMotionProp ?? ownReducedMotion;
  const isInViewport = useInViewport(wrapRef);

  // Heavy coastline geometry: computed once across the component's life
  const coastRings = useMemo(buildCoastlineRings, []);

  /* Keep a ref of the active city for the animate loop without re-subscribing */
  useEffect(() => {
    activeRef.current = activeLocation;
  }, [activeLocation]);

  /* Project every marker to screen space and position its HTML label.
     Front-facing labels show; back-hemisphere labels fade to 0. Writes
     straight to element.style (no per-frame setState). Reused scratch
     vectors avoid per-frame allocation. */
  const projectLabels = useMemo(() => {
    const world = new Vector3();
    const normal = new Vector3();
    const camDir = new Vector3();
    const projected = new Vector3();

    return () => {
      const canvas = canvasRef.current;
      const cam = camera.current;
      const grp = group.current;
      if (!canvas || !cam || !grp) return;

      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (w === 0 || h === 0) return;

      grp.updateMatrixWorld();
      const activeId = activeRef.current;

      for (const city of CITIES) {
        const marker = markers.current[city.id];
        const el = labelEls.current[city.id];
        if (!marker || !el) continue;

        // World position of the marker (group rotation applied)
        world.copy(marker.position).applyMatrix4(grp.matrixWorld);

        // Surface normal at the marker, in world space, for back-side test
        normal.copy(marker.position).normalize().transformDirection(grp.matrixWorld);
        // Direction from marker toward the camera
        camDir.copy(cam.position).sub(world).normalize();
        const facing = normal.dot(camDir); // > 0 → front hemisphere

        // Project to NDC then to screen pixels. A per-city offset separates
        // geographically close label pairs.
        projected.copy(world).project(cam);
        const [ox, oy] = city.labelOffset || [0, 0];
        const x = (projected.x * 0.5 + 0.5) * w + ox;
        const y = (-projected.y * 0.5 + 0.5) * h + oy;

        // Smooth opacity toward target (front visible, back hidden).
        // A small soft edge near the limb avoids a hard pop.
        const target = facing > 0.08 ? 1 : 0;
        const current = labelOpacity.current[city.id] ?? 0;
        const next = current + (target - current) * LABEL_FADE;
        labelOpacity.current[city.id] = next;

        el.style.transform = `translate(-50%, -50%) translate(${x}px, ${y}px)`;
        el.style.opacity = next.toFixed(3);
        // Lift the active label above neighbours for legibility
        el.style.zIndex = city.id === activeId ? '3' : '2';
        if (city.id === activeId) {
          el.classList.add('cities__globe-label--active');
        } else {
          el.classList.remove('cities__globe-label--active');
        }
      }
    };
  }, []);

  /* Target group rotation that brings a city's local unit vector to face the
     camera (+Z, since the camera sits on +Z looking at the origin). Returns
     null when the city id is unknown. Reads the marker's local position so it
     matches whatever toXYZ convention the geometry was built with. */
  const computeFocusTarget = useMemo(() => {
    const p = new Vector3();
    return id => {
      const marker = markers.current[id];
      if (!marker) return null;
      p.copy(marker.position).normalize();
      // atan2(-x, z) yaws the point to +Z; atan2(y, hypot(x,z)) pitches it up.
      const targetRotY = Math.atan2(-p.x, p.z);
      const targetRotX = Math.atan2(p.y, Math.hypot(p.x, p.z));
      return { x: targetRotX, y: targetRotY };
    };
  }, []);

  /* Advance all futuristic motion (graticule shimmer, radar pings, arc flow)
     by the accumulated clock. Imperative only — no setState. Called from the
     rAF loop; never invoked under reduced motion (motion gated by the loop). */
  const updateFuturisticMotion = useMemo(() => {
    return () => {
      const t = clockRef.current;
      const activeId = activeRef.current;

      // Graticule: a gentle breathing shimmer around the base opacity.
      if (graticuleMat.current) {
        const shimmer = Math.sin(t * 0.0009) * GRATICULE_SHIMMER;
        graticuleMat.current.opacity = GRATICULE_OPACITY + shimmer;
      }

      // Radar pings: expand + fade on a loop. Active city pings strongest;
      // others stay faint. Phase-offset per ring so they don't pulse in sync.
      let idx = 0;
      for (const id in pingRings.current) {
        const ring = pingRings.current[id];
        if (!ring) continue;
        const isActive = id === activeId;
        const phase = ((t + idx * 520) % PING_PERIOD) / PING_PERIOD; // 0..1
        idx += 1;
        const scale =
          PING_MIN_SCALE + (PING_MAX_SCALE - PING_MIN_SCALE) * phase;
        ring.scale.setScalar(scale);
        const peak = isActive ? PING_ACTIVE_OPACITY : PING_IDLE_OPACITY;
        // Fade out as it expands (1 → 0 across the cycle).
        ring.material.opacity = peak * (1 - phase);
        ring.visible = true;
      }

      // Flowing arcs: rewrite each comet's vertices to ride along its path.
      for (const pulse of flowPulses.current) {
        const { mesh, path, color } = pulse;
        const pos = mesh.geometry.attributes.position;
        const col = mesh.geometry.attributes.color;
        const head = (t * FLOW_SPEED) % 1; // 0..1 progress along the arc
        const lastIndex = path.length - 1;
        for (let k = 0; k <= FLOW_TAIL; k += 1) {
          // Sample slightly behind the head for each tail vertex.
          const back = head - (k / FLOW_TAIL) * 0.12;
          const clamped = Math.max(0, Math.min(1, back));
          const fIndex = clamped * lastIndex;
          const i0 = Math.floor(fIndex);
          const i1 = Math.min(lastIndex, i0 + 1);
          const f = fIndex - i0;
          const a = path[i0];
          const b = path[i1];
          const x = a.x + (b.x - a.x) * f;
          const y = a.y + (b.y - a.y) * f;
          const z = a.z + (b.z - a.z) * f;
          pos.setXYZ(k, x, y, z);
          // Comet fade: bright head, dim tail.
          const fade = 1 - k / FLOW_TAIL;
          col.setXYZ(k, color.r * fade, color.g * fade, color.b * fade);
        }
        pos.needsUpdate = true;
        col.needsUpdate = true;
        mesh.visible = true;
      }
    };
  }, []);

  /* ── Scene setup (once) ── */
  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    const size = Math.max(1, wrap?.clientWidth || 480);

    renderer.current = new WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
    });
    renderer.current.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.current.setSize(size, size, false);

    camera.current = new PerspectiveCamera(38, 1, 0.1, 100);
    camera.current.position.z = 3.1;

    scene.current = new Scene();

    group.current = new Group();
    // Orient so the Atlantic → Europe → Asia journey faces the camera
    group.current.rotation.y = -1.15;
    group.current.rotation.x = 0.35;
    scene.current.add(group.current);

    // Globe body — a deep, polished navy sphere (museum-vitrine reading)
    const globeGeometry = new SphereBufferGeometry(GLOBE_RADIUS, 64, 64);
    const globeMaterial = new MeshPhongMaterial({
      color: threeColor(RGB_NAVY_DEEP),
      emissive: threeColor(RGB_NAVY_EMISSIVE),
      emissiveIntensity: 0.6,
      specular: threeColor(RGB_RIM),
      shininess: 10,
    });
    const globe = new Mesh(globeGeometry, globeMaterial);
    group.current.add(globe);
    disposables.current.push(globeGeometry, globeMaterial);

    // Soft halo — a larger BackSide shell with additive sapphire blending reads
    // as a subtle atmospheric glow around the dark globe. Kept faint.
    const atmosphereGeometry = new SphereBufferGeometry(ATMOSPHERE_RADIUS, 48, 48);
    const atmosphereMaterial = new MeshBasicMaterial({
      color: threeColor(RGB_RIM),
      side: BackSide,
      blending: AdditiveBlending,
      transparent: true,
      opacity: 0.2,
      depthWrite: false,
    });
    const atmosphere = new Mesh(atmosphereGeometry, atmosphereMaterial);
    // Halo belongs to the scene (not the rotating group) so it stays camera-
    // stable; the group only carries surface detail.
    scene.current.add(atmosphere);
    disposables.current.push(atmosphereGeometry, atmosphereMaterial);

    // Crisp holographic rim — a thin BackSide shell tight to the globe. Because
    // BackSide additive shells brighten where the surface grazes the camera,
    // a small radius gives a sharp cyan-sapphire fresnel silhouette (hologram
    // edge) without washing the disc. Camera-stable like the halo.
    const rimGeometry = new SphereBufferGeometry(RIM_RADIUS, 64, 64);
    const rimMaterial = new MeshBasicMaterial({
      color: threeColor(RGB_RIM_LIGHT),
      side: BackSide,
      blending: AdditiveBlending,
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
    });
    const rim = new Mesh(rimGeometry, rimMaterial);
    scene.current.add(rim);
    disposables.current.push(rimGeometry, rimMaterial);

    // Holographic graticule — a sparse, faint lat/long grid just above the
    // surface. This is the core "futuristic instrument" cue. Lives in the
    // group so it spins with the globe.
    const graticuleGeometry = buildGraticule(GRATICULE_RADIUS);
    const graticuleMaterial = new LineBasicMaterial({
      color: threeColor(RGB_GRATICULE),
      transparent: true,
      opacity: GRATICULE_OPACITY,
      blending: AdditiveBlending,
      depthWrite: false,
    });
    const graticule = new LineSegments(graticuleGeometry, graticuleMaterial);
    group.current.add(graticule);
    graticuleMat.current = graticuleMaterial;
    disposables.current.push(graticuleGeometry, graticuleMaterial);

    // Coastlines — luminous blue-white LineLoops drawn just above the surface
    const coastMaterial = new LineBasicMaterial({
      color: threeColor(RGB_COAST),
      transparent: true,
      opacity: 0.6,
    });
    disposables.current.push(coastMaterial);
    for (const positions of coastRings) {
      const geometry = new BufferGeometry();
      geometry.setAttribute(
        'position',
        new Float32BufferAttribute(positions, 3)
      );
      const loop = new LineLoop(geometry, coastMaterial);
      group.current.add(loop);
      disposables.current.push(geometry);
    }

    // Journey arcs between consecutive cities (sorted by year)
    const journey = [...CITIES].sort((a, b) => a.year - b.year);
    const arcMaterial = new LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.9,
      blending: AdditiveBlending,
      depthWrite: false,
    });
    disposables.current.push(arcMaterial);
    // A small glowing pulse rides each arc. We build it as a short LineSegments
    // "comet" (head + fading tail) whose vertices we rewrite each frame from a
    // sampled arc path. Thin + additive so it glows without thickening the arc.
    flowPulses.current = [];
    const pulseGeometry = () => {
      const g = new BufferGeometry();
      const verts = (FLOW_TAIL + 1) * 3;
      g.setAttribute('position', new Float32BufferAttribute(new Float32Array(verts), 3));
      g.setAttribute('color', new Float32BufferAttribute(new Float32Array(verts), 3));
      return g;
    };
    for (let i = 1; i < journey.length; i += 1) {
      const arcGeometry = buildArc(journey[i - 1], journey[i]);
      const arc = new Line(arcGeometry, arcMaterial);
      group.current.add(arc);
      disposables.current.push(arcGeometry);

      // Travelling pulse for this arc (animated only when motion is allowed).
      const path = buildArcPath(journey[i - 1], journey[i]);
      const pColor = threeColor(ARC_RGB[journey[i].accent] || RGB_ARC_SAPPHIRE);
      const pGeometry = pulseGeometry();
      const pMaterial = new LineBasicMaterial({
        vertexColors: true,
        transparent: true,
        opacity: 0.95,
        blending: AdditiveBlending,
        depthWrite: false,
      });
      const pulse = new LineSegments(pGeometry, pMaterial);
      pulse.visible = false; // shown only while animating
      group.current.add(pulse);
      flowPulses.current.push({ mesh: pulse, path, color: pColor });
      disposables.current.push(pGeometry, pMaterial);
    }

    // City markers — glowing dots that read on the navy sphere. Each marker is
    // a small bright core plus a larger additive halo for the glow.
    const markerGeometry = new SphereBufferGeometry(0.022, 16, 16);
    const glowGeometry = new SphereBufferGeometry(0.05, 16, 16);
    // Thin radar-ping ring, oriented tangent to the sphere at each marker.
    const ringGeometry = new RingBufferGeometry(0.032, 0.04, 40);
    disposables.current.push(markerGeometry, glowGeometry, ringGeometry);
    markers.current = {};
    markerGlows.current = {};
    pingRings.current = {};
    for (const city of CITIES) {
      const rgb = MARKER_RGB[city.accent] || RGB_STUDY;
      const isWork = city.accent !== 'muted';

      const material = new MeshBasicMaterial({
        color: threeColor(rgb),
        transparent: true,
        opacity: isWork ? 1 : 0.92,
      });
      const marker = new Mesh(markerGeometry, material);
      const pos = toXYZ(city.lonlat[0], city.lonlat[1], MARKER_RADIUS);
      marker.position.copy(pos);
      marker.userData.baseScale = 1;
      group.current.add(marker);
      markers.current[city.id] = marker;
      disposables.current.push(material);

      const glowMaterial = new MeshBasicMaterial({
        color: threeColor(rgb),
        transparent: true,
        opacity: isWork ? 0.45 : 0.3,
        blending: AdditiveBlending,
        depthWrite: false,
      });
      const glow = new Mesh(glowGeometry, glowMaterial);
      glow.position.copy(pos);
      group.current.add(glow);
      markerGlows.current[city.id] = glow;
      disposables.current.push(glowMaterial);

      // Radar-ping ring: a thin ring sitting flat on the sphere surface,
      // animated to expand + fade in the rAF loop. Reduced motion keeps it
      // hidden. Cyan ping tone for the futuristic instrument feel.
      const ringMaterial = new MeshBasicMaterial({
        color: threeColor(RGB_PING),
        transparent: true,
        opacity: 0,
        side: DoubleSide,
        blending: AdditiveBlending,
        depthWrite: false,
      });
      const ring = new Mesh(ringGeometry, ringMaterial);
      ring.position.copy(pos);
      // Orient the ring tangent to the sphere (face along the surface normal).
      ring.lookAt(0, 0, 0);
      ring.visible = false; // shown only while animating
      group.current.add(ring);
      pingRings.current[city.id] = ring;
      disposables.current.push(ringMaterial);
    }

    return () => {
      for (const item of disposables.current) {
        if (item && typeof item.dispose === 'function') item.dispose();
      }
      disposables.current = [];
      cleanScene(scene.current);
      cleanRenderer(renderer.current);
    };
    // coastRings is stable (memoized once); intentionally one-time setup
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Lights (once) ── */
  useEffect(() => {
    const keyLight = new DirectionalLight(threeColor(RGB_KEY_LIGHT), 0.8);
    keyLight.position.set(3, 4, 5);

    const ambientLight = new AmbientLight(threeColor(RGB_AMBIENT), 0.55);

    lights.current = [keyLight, ambientLight];
    lights.current.forEach(light => scene.current.add(light));

    return () => removeLights(lights.current);
  }, []);

  /* ── Responsive sizing ── */
  useEffect(() => {
    const handleResize = () => {
      const wrap = wrapRef.current;
      if (!wrap || !renderer.current || !camera.current) return;
      const size = Math.max(1, wrap.clientWidth);
      renderer.current.setSize(size, size, false);
      camera.current.aspect = 1;
      camera.current.updateProjectionMatrix();
      if (prefersReducedMotion) {
        projectLabels();
        renderer.current.render(scene.current, camera.current);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [prefersReducedMotion, projectLabels]);

  /* ── Highlight the active marker ── */
  useEffect(() => {
    const apply = () => {
      Object.entries(markers.current).forEach(([id, marker]) => {
        const isActive = id === activeLocation;
        marker.scale.setScalar(isActive ? 1.9 : 1);
        const glow = markerGlows.current[id];
        if (glow) glow.scale.setScalar(isActive ? 1.6 : 1);
      });
      // Under reduced motion the rAF tween doesn't run, so snap the focused
      // city to the camera-facing point here (non-jarring: no animation).
      if (prefersReducedMotion && activeLocation && group.current) {
        const target = computeFocusTarget(activeLocation);
        if (target) {
          group.current.rotation.y = target.y;
          group.current.rotation.x = target.x;
        }
      }
      // Re-render a frame so the change shows even when paused / reduced motion
      if (renderer.current && scene.current && camera.current) {
        projectLabels();
        renderer.current.render(scene.current, camera.current);
      }
    };
    if (Object.keys(markers.current).length) apply();
  }, [activeLocation, projectLabels, prefersReducedMotion, computeFocusTarget]);

  /* ── Drag-to-rotate with inertia (raw pointer events) ──
     A gesture becomes a globe-drag only after DRAG_THRESHOLD px of movement,
     so taps and the start of a vertical scroll don't hijack the page. On
     release the latest pointer velocity is carried into the animate loop as
     momentum (decayed each frame), so a flick spins fast then eases out.
     Pointer capture is released on up/cancel so the page stays scrollable. */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const renderStatic = () => {
      if (prefersReducedMotion && renderer.current && scene.current) {
        projectLabels();
        renderer.current.render(scene.current, camera.current);
      }
    };

    const onPointerDown = event => {
      const st = dragState.current;
      st.pointerDown = true;
      st.dragging = false;
      st.startX = event.clientX;
      st.startY = event.clientY;
      st.lastX = event.clientX;
      st.lastY = event.clientY;
      // A fresh grab cancels any leftover momentum.
      st.velX = 0;
      st.velY = 0;
    };

    const onPointerMove = event => {
      const st = dragState.current;
      if (!st.pointerDown || !group.current) return;

      // Promote to a drag only once movement clears the threshold.
      if (!st.dragging) {
        const totalDx = event.clientX - st.startX;
        const totalDy = event.clientY - st.startY;
        if (Math.hypot(totalDx, totalDy) < DRAG_THRESHOLD) return;
        st.dragging = true;
        st.paused = true;
        canvas.setPointerCapture?.(event.pointerId);
      }

      const dx = event.clientX - st.lastX;
      const dy = event.clientY - st.lastY;
      st.lastX = event.clientX;
      st.lastY = event.clientY;

      const vy = dx * DRAG_SENSITIVITY;
      group.current.rotation.y += vy;
      group.current.rotation.x = Math.max(
        -MAX_TILT,
        Math.min(MAX_TILT, group.current.rotation.x + dy * DRAG_SENSITIVITY)
      );

      // Remember the most recent step as the release velocity (reduced-motion
      // gets no momentum, but dragging itself stays responsive).
      st.velX = prefersReducedMotion ? 0 : vy;
      st.velY = prefersReducedMotion
        ? 0
        : dy * DRAG_SENSITIVITY;

      renderStatic();
    };

    const endDrag = event => {
      const st = dragState.current;
      if (!st.pointerDown) return;
      st.pointerDown = false;
      st.dragging = false;
      st.paused = false;
      if (event?.pointerId != null) {
        canvas.releasePointerCapture?.(event.pointerId);
      }
      // Momentum (if any) is consumed by the animate loop; reduced-motion
      // already cleared the velocities above.
    };

    canvas.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);

    return () => {
      canvas.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
    };
  }, [prefersReducedMotion, projectLabels]);

  /* ── Best-effort hover → activeLocation via raycasting markers ── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !onActivate) return undefined;

    const raycaster = new Raycaster();
    const pointer = new Vector2();

    const pickAt = event => {
      if (dragState.current.dragging || !camera.current) return;
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(pointer, camera.current);
      const meshes = Object.values(markers.current);
      const hits = raycaster.intersectObjects(meshes, false);
      if (hits.length) {
        const id = Object.keys(markers.current).find(
          key => markers.current[key] === hits[0].object
        );
        if (id) {
          // Record that THIS activation came from a direct-on-globe hover so
          // the rotate-to-face tween knows to leave the globe still.
          raycastHoverRef.current = id;
          if (id !== activeRef.current) onActivate(id);
        }
      } else {
        // Pointer left every marker: clear the direct-hover source and let the
        // shared deactivate flow run.
        raycastHoverRef.current = null;
        if (activeRef.current) onDeactivate?.();
      }
    };

    const onLeave = () => {
      raycastHoverRef.current = null;
      if (activeRef.current) onDeactivate?.();
    };

    canvas.addEventListener('pointermove', pickAt);
    canvas.addEventListener('pointerleave', onLeave);
    return () => {
      canvas.removeEventListener('pointermove', pickAt);
      canvas.removeEventListener('pointerleave', onLeave);
    };
  }, [onActivate, onDeactivate]);

  /* ── Track pointer hover over the canvas (Task 2: pause auto-rotate) ── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const onEnter = () => {
      pointerOverRef.current = true;
    };
    const onLeave = () => {
      pointerOverRef.current = false;
    };

    canvas.addEventListener('pointerenter', onEnter);
    canvas.addEventListener('pointerleave', onLeave);
    return () => {
      canvas.removeEventListener('pointerenter', onEnter);
      canvas.removeEventListener('pointerleave', onLeave);
    };
  }, []);

  /* ── Animate loop (rAF only while in viewport + motion allowed) ──
     Per-frame precedence:
       1. active drag → user spins freely (handled in the pointer effect)
       2. flick momentum → decays out
       3. a city is focused (list/raycast) → ease-to-face + hold (Task 4)
       4. pointer is over the globe → pause (Task 2)
       5. otherwise → gentle auto-rotate */
  useEffect(() => {
    let animation;

    const animate = ts => {
      animation = requestAnimationFrame(animate);
      // Delta-time so phases stay frame-rate independent and don't jump after
      // the tab is backgrounded. Clamp the first/long frames.
      const now = ts || 0;
      const dt = lastTsRef.current ? Math.min(50, now - lastTsRef.current) : 16;
      lastTsRef.current = now;
      clockRef.current += dt;
      updateFuturisticMotion();
      const st = dragState.current;
      if (group.current && !st.dragging) {
        const hasMomentum =
          Math.abs(st.velX) > MOMENTUM_MIN ||
          Math.abs(st.velY) > MOMENTUM_MIN;
        // Rotate-to-face only when the active city came from the right-column
        // list / keyboard focus — i.e. NOT from a direct hover on the globe's
        // marker. A direct hover keeps raycastHoverRef === the active id; in
        // that case we highlight + pause but leave the globe still so the
        // marker stays under the cursor (rotating it would steal the hover).
        const shouldFocus =
          activeRef.current && activeRef.current !== raycastHoverRef.current;
        const focusTarget = shouldFocus
          ? computeFocusTarget(activeRef.current)
          : null;

        if (hasMomentum) {
          // Flick momentum: keep spinning, decaying smoothly each frame.
          group.current.rotation.y += st.velX;
          group.current.rotation.x = Math.max(
            -MAX_TILT,
            Math.min(MAX_TILT, group.current.rotation.x + st.velY)
          );
          st.velX *= MOMENTUM_DECAY;
          st.velY *= MOMENTUM_DECAY;
          if (Math.abs(st.velX) <= MOMENTUM_MIN) st.velX = 0;
          if (Math.abs(st.velY) <= MOMENTUM_MIN) st.velY = 0;
        } else if (focusTarget) {
          // A city is focused: ease its marker to the camera-facing point and
          // hold. Wrap deltas so we always rotate the short way around.
          const dy = wrapAngle(focusTarget.y - group.current.rotation.y);
          const dx = focusTarget.x - group.current.rotation.x;
          group.current.rotation.y += dy * FOCUS_EASE;
          group.current.rotation.x += dx * FOCUS_EASE;
        } else if (!pointerOverRef.current) {
          // Idle: gentle auto-rotate (paused while hovering the globe).
          group.current.rotation.y += AUTO_ROTATE_SPEED;
        }
      }
      projectLabels();
      renderer.current.render(scene.current, camera.current);
    };

    if (!prefersReducedMotion && isInViewport) {
      lastTsRef.current = 0; // reset delta baseline on (re)start
      animate();
    } else if (renderer.current) {
      // Static frame for reduced-motion / off-screen. No pings, no flow, no
      // shimmer — the calm holographic globe with its grid + rim at rest.
      if (graticuleMat.current) graticuleMat.current.opacity = GRATICULE_OPACITY;
      for (const id in pingRings.current) {
        if (pingRings.current[id]) pingRings.current[id].visible = false;
      }
      for (const pulse of flowPulses.current) {
        if (pulse?.mesh) pulse.mesh.visible = false;
      }
      // Under reduced motion we still bring a focused city to the front, but
      // snap instead of tween.
      const focusTarget =
        prefersReducedMotion && activeRef.current
          ? computeFocusTarget(activeRef.current)
          : null;
      if (focusTarget && group.current) {
        group.current.rotation.y = focusTarget.y;
        group.current.rotation.x = focusTarget.x;
      }
      projectLabels();
      renderer.current.render(scene.current, camera.current);
    }

    return () => cancelAnimationFrame(animation);
  }, [
    isInViewport,
    prefersReducedMotion,
    projectLabels,
    computeFocusTarget,
    updateFuturisticMotion,
  ]);

  return (
    <div className="cities__globe-stage" ref={wrapRef}>
      <canvas
        ref={canvasRef}
        className="cities__globe-canvas"
        aria-hidden="true"
      />
      {/* Tracking city labels — decorative; the places list is the
          accessible representation, so this layer is hidden from AT. */}
      <div
        className="cities__globe-labels"
        ref={labelLayerRef}
        aria-hidden="true"
      >
        {CITIES.map(city => (
          <span
            key={city.id}
            className={`cities__globe-label cities__globe-label--${city.accent}`}
            ref={el => {
              if (el) labelEls.current[city.id] = el;
            }}
          >
            <span className="cities__globe-label-tick" />
            <span className="cities__globe-label-name">
              {LABEL_TEXT[city.id].name}
            </span>
            <span className="cities__globe-label-meta">
              {LABEL_TEXT[city.id].meta}
            </span>
            {/* Linked experience — revealed/emphasised on the active label */}
            <span className="cities__globe-label-role">
              {LABEL_TEXT[city.id].role}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
};

/* Display text for the tracking labels (decorative; the list carries the
   full accessible data). Kept here so the globe is self-contained. `role`
   mirrors the LOCATIONS roles in index.js and surfaces on the active label. */
const LABEL_TEXT = {
  montreal: {
    name: 'Montreal',
    meta: 'Canada',
    role: 'Exchange · Concordia University',
  },
  paris: {
    name: 'Paris',
    meta: 'France',
    role: 'Freelance Full-Stack Engineer',
  },
  barcelona: {
    name: 'Barcelona',
    meta: 'Spain',
    role: 'Exchange · Universitat Politècnica de Catalunya',
  },
};

export default CitiesGlobe;
